import './SonarCard.scss';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { LoadingSpinner } from '@ui/LoadingSpinner';

import { Card, CardProps, Col, Row, Space } from 'antd';
import React, { forwardRef, ReactNode, useImperativeHandle, useRef } from 'react';

export type SonarCardProps = CardProps & {
	extraAsRow?: boolean;
	footer?: ReactNode;
	icon?: ReactNode;
	noSpacing?: boolean;
	noSpacingX?: boolean;
	title?: string | ReactNode;
	titleClassName?: string;
	useSpinnerLoading?: boolean;
};

export const SonarCard = forwardRef<HTMLDivElement | null, SonarCardProps>(({
	children,
	className,
	extra,
	extraAsRow = false,
	footer,
	icon,
	loading,
	noSpacing,
	noSpacingX,
	title,
	titleClassName = 'h1',
	useSpinnerLoading,
	...rest
}, ref) => {
	let cardTitle = typeof title === 'string' ?
		<div className={ classNames(titleClassName, { 'with-icon': Boolean(icon) }) }>{ title }</div> :
		title;

	if (icon) {
		cardTitle = <>{ icon }{ cardTitle }</>;
	}

	const useCustomTitle = Boolean(cardTitle) && Boolean(extra);

	const customTitle = extraAsRow ?
		<Space
			direction='vertical'
			style={ { width: '100%' } }>
			<Row>{ cardTitle }</Row>
			<Row
				align='middle'
				justify='space-between'>{ extra }
			</Row>
		</Space> :
		<Row
			align='middle'
			justify='space-between'>
			<Col>{ cardTitle }</Col>
			<Col>{ extra }</Col>
		</Row>;

	const displayTitle = useCustomTitle ?
		customTitle	 :
		cardTitle;

	const cardContainerRef = useRef<HTMLDivElement>(null);

	useImperativeHandle(ref, () => cardContainerRef.current, []);

	return (
		<Card
			className={ classNames('sonar-card', {
				'no-spacing': noSpacing,
				'no-spacing-x': noSpacingX }, className) }
			extra={ useCustomTitle ? undefined : extra }
			loading={ useSpinnerLoading ? undefined : loading }
			ref={ cardContainerRef }
			title={ displayTitle }
			{ ...rest }>
			{ useSpinnerLoading ?
				<LoadingSpinner
					minDuration={ 1000 }
					opaque
					spinning={ loading }>
					{ children }
				</LoadingSpinner> : children }
			{ cardContainerRef.current && footer &&
				createPortal(<div className='sonar-card-footer'>{ footer }</div>, cardContainerRef.current) }
		</Card>
	);
});
